import { call, put, takeLatest } from 'redux-saga/effects';

import { captureException } from '../../../client/sentry';
import { graphql } from '../../../globals/api/requests';
import { promptToast } from '../../../components/toast-container/actions';
import { isValidEmail } from '../../account/helpers';

import storeReviewsFragment from '../../store/components/user-reviews/fragment.graphql';
import productReviewsFragment from '../../product/components/user-reviews/fragment.graphql';

import {
  CREATE_REVIEW,
  DELETE_REVIEW,
  EDIT_REVIEW,
  REPORT_REVIEW,
  closeReviewDialog,
  updateReviewData,
  reviewRequestError,
  reportReviewSuccess,
  reportReviewFailed,
} from '../actions';

import { setDisplayName } from '../../account/settings/actions/display-name';

function* updateUserReviewData(id, type, actionType) {
  try {
    const reviewType = type.toLowerCase();

    const fragment = {
      PRODUCT: productReviewsFragment,
      STORE: storeReviewsFragment,
    }[type];

    if (!fragment) {
      throw new Error('Missing or invalid type for review update');
    }

    const query = `
      query updateUserReviewData($offset: Int!, $id: Int!) {
        ${reviewType}(id: $id) {
          ...${reviewType}UserReviews
        }
      },
      ${fragment}`;

    const response = yield call(graphql, query, { id, offset: 0 });

    if (response.errors) {
      yield call(captureException, response.errors);
      return;
    }

    // TODO: Change the GraphQL data directly instead, and make the data container to re-render
    yield put(
      updateReviewData(
        reviewType,
        response.data[reviewType].userReviews,
        id,
        actionType
      )
    );

    yield call(
      window.__history.replace,

      window.__history.location.pathname + window.__history.location.search
    );
  } catch (e) {
    yield call(captureException, e);
  }
}

export function* createReview({
  belongsToId, // i.e. product id, store id
  rating,
  reviewType: type,
  reviewText,
  purchaseDate,
  displayName,
}) {
  try {
    const response = yield call(
      graphql,
      `mutation reviewCreate(
        $belongsToId: Int!,
        $type: reviewType!,
        $rating: Int!,
        $reviewText: String!,
        $purchaseDate: String
        $displayName: String) {
          reviewCreate(
            type: $type,
            belongsToId: $belongsToId,
            rating: $rating,
            reviewText: $reviewText,
            purchaseDate: $purchaseDate
            displayName: $displayName) {
              id
              rating
              reviewText
              createdAt
              updatedAt
              user {
                id
                name
                displayName
                reviewCount
                avatar(width: _100)
              }
          }
     }`,
      {
        type,
        belongsToId,
        rating,
        reviewText,
        purchaseDate,
        displayName,
      }
    );

    if (response.errors) {
      yield put(reviewRequestError());
      yield call(captureException, response.errors);
      return;
    }

    if (displayName) {
      yield put(setDisplayName(displayName));
    }

    yield put(closeReviewDialog());
    yield call(updateUserReviewData, belongsToId, type, 'CREATE');

    yield put(
      promptToast(
        'success',
        window.APP_GLOBALS.t('reviews', 'dialog.create-success')
      )
    );
  } catch (e) {
    yield put(reviewRequestError());
    yield call(captureException, e);
  }
}

export function* editReview({
  reviewId,
  belongsToId, // i.e. product id, store id
  reviewType: type,
  rating,
  reviewText,
  purchaseDate,
}) {
  try {
    const response = yield call(
      graphql,
      `mutation reviewEdit(
        $reviewId: String!,
        $rating: Int!,
        $reviewText: String!,
        $purchaseDate: String,
      ) {
        reviewEdit(
          reviewId: $reviewId,
          rating: $rating,
          reviewText: $reviewText,
          purchaseDate: $purchaseDate,
        ) {
          id
          rating
          reviewText
          createdAt
          updatedAt
          user {
            id
            name
            displayName
            reviewCount
            avatar(width: _100)
          }
        }
      }`,
      {
        reviewId,
        rating,
        reviewText,
        purchaseDate,
      }
    );

    if (response.errors) {
      yield put(reviewRequestError());
      yield call(captureException, response.errors);
      return;
    }

    yield put(closeReviewDialog());
    yield call(updateUserReviewData, belongsToId, type, 'EDIT');

    yield put(
      promptToast(
        'success',
        window.APP_GLOBALS.t('reviews', 'dialog.edit-success')
      )
    );
  } catch (e) {
    yield call(captureException, e);
  }
}

export function* deleteReview({ reviewId, belongsToId, reviewType: type }) {
  try {
    const response = yield call(
      graphql,
      `mutation reviewDelete(
        $reviewId: String!,
      ) {
        reviewDelete(
          reviewId: $reviewId,
        ) {
          success
        }
      }`,
      {
        reviewId,
      }
    );

    if (response.errors) {
      yield put(reviewRequestError());
      yield call(captureException, response.errors);
      return;
    }

    yield put(closeReviewDialog());
    yield call(updateUserReviewData, belongsToId, type, 'DELETE');

    yield put(
      promptToast(
        'success',
        window.APP_GLOBALS.t('reviews', 'dialog.delete-success')
      )
    );
  } catch (e) {
    yield call(captureException, e);
  }
}

export function* reportReview({ reviewId, message, email }) {
  if (!email || !isValidEmail(email)) {
    yield put(reportReviewFailed('error.missing.email'));
    return;
  }
  if (!message) {
    yield put(reportReviewFailed('error.missing.message'));
    return;
  }
  try {
    const response = yield call(
      graphql,
      `mutation reviewReport(
        $reviewId: String!,
        $message: String!,
        $email: String!,
      ) {
        reviewReport(
          reviewId: $reviewId,
          message: $message,
          email: $email,
        ) {
          success
        }
      }`,
      { reviewId, message, email }
    );
    if (
      response &&
      response.data &&
      response.data.reviewReport &&
      response.data.reviewReport.success
    ) {
      yield put(reportReviewSuccess());
      yield put(
        promptToast(
          'success',
          window.APP_GLOBALS.t('feedback', 'report-reviews.success')
        )
      );
    } else {
      yield put(reportReviewFailed(response ? response.errors : undefined));
    }
  } catch (err) {
    yield call(captureException, err);
    yield put(reportReviewFailed(err.message));
  }
}

function* mySaga() {
  yield takeLatest(CREATE_REVIEW, createReview);
  yield takeLatest(EDIT_REVIEW, editReview);
  yield takeLatest(DELETE_REVIEW, deleteReview);
  yield takeLatest(REPORT_REVIEW, reportReview);
}

export default mySaga;
